// Manufacturing projects for the screens: one indexProjects() (shared/projects.ts) over the synced tables and the BOM
// index, rebuilt only when one of them changes and shared by every component that asks. Readiness, scopes and the
// used-in index are worked out on demand and cached on it, so a page shows at once and a tab switch costs nothing.
import { type BomIndex, type BomRow, familyKey } from "../../../shared/boms.ts";
import type { Item, Project } from "../../../shared/db.gen.ts";
import { familyProjects } from "../../../shared/project-reach.ts";
import {
  type GapTarget,
  indexProjects,
  type PartProjects,
  type ProjectData,
  type ProjectIndex,
  partProjects,
  projectOfItem,
  reachOf,
  targetPath,
  type UsedIn,
  usedInIndex,
} from "../../../shared/projects.ts";
import { useBomIndex } from "./boms.ts";
import { useRows } from "./sync.ts";

let last: { inputs: unknown[]; index: ProjectIndex; usedIn?: Map<string, UsedIn> } | null = null;

export function useProjectIndex(): ProjectIndex {
  const ix: BomIndex = useBomIndex();
  const projects = useRows("project");
  const projectProducts = useRows("project_product");
  const products = useRows("shopify_product");
  const items = useRows("item");
  const vendors = useRows("vendor");
  const vendorItems = useRows("vendor_item");
  const specs = useRows("spec");
  const specFiles = useRows("spec_file");
  const specRevisions = useRows("spec_revision");
  const specRevisionFiles = useRows("spec_revision_file"); // Packaging --- what a released revision holds
  const locations = useRows("location");
  const levels = useRows("stock_level");
  const balances = useRows("stock_balance");
  const shipments = useRows("consign_shipment");
  const shipmentLines = useRows("consign_shipment_line");
  const makeOrders = useRows("make_order");
  const pos = useRows("po");
  const changeNotices = useRows("change_notice");
  const changeNoticeItems = useRows("change_notice_item");
  const inputs = [
    ix,
    projects,
    projectProducts,
    products,
    items,
    vendors,
    vendorItems,
    specs,
    specFiles,
    specRevisions,
    specRevisionFiles,
    locations,
    levels,
    balances,
    shipments,
    shipmentLines,
    makeOrders,
    pos,
    changeNotices,
    changeNoticeItems,
  ];
  if (!last || last.inputs.some((x, i) => x !== inputs[i])) {
    const stock = [
      ...levels.map((s) => ({ item_id: s.item_id, location_id: s.location_id, qty: s.on_hand })),
      ...balances.map((b) => ({ item_id: b.item_id, location_id: b.location_id, qty: b.qty })),
    ];
    const data: ProjectData = {
      projects,
      projectProducts,
      products,
      items,
      vendors,
      vendorItems,
      specs,
      specFiles,
      specRevisions,
      specRevisionFiles,
      locations,
      stock,
      shipments,
      shipmentLines,
      makeOrders,
      pos,
      changeNotices,
      changeNoticeItems,
    };
    last = { inputs, index: indexProjects(ix, data) };
  }
  return last.index;
}

/** Where each part ends up (projects and products), for the Parts library; built once per index. */
export function useUsedIn(): Map<string, UsedIn> {
  const px = useProjectIndex();
  if (last && last.index === px) return (last.usedIn ??= usedInIndex(px));
  return usedInIndex(px);
}

/** Each part's projects (in use, only on a draft), by project name (shared/project-reach.ts); worked out once per index. */
export const usePartProjects = (): Map<string, PartProjects> => partProjects(useProjectIndex());

/**
 * Any item's projects, as inProject() filters by them: a part's from the BOMs that use it; a sell item's own
 * (projectOfItem()), then those whose builds use it as a part.
 */
export function projectsOfItem(
  px: ProjectIndex,
  item: Pick<Item, "id" | "source" | "shopify_product_id" | "project_id">,
): Pick<PartProjects, "inUse" | "onDraft"> {
  if (item.source === "local") return partProjects(px).get(item.id) ?? { inUse: [], onDraft: [] };
  const own = projectOfItem(px, item);
  const out = { inUse: own ? [own] : [], onDraft: [] as string[] };
  for (const [id, r] of reachOf(px).byProject)
    if (id !== own && r.parts.has(item.id)) out.inUse.push(id);
    else if (id !== own && r.onDraft.has(item.id)) out.onDraft.push(id);
  return out;
}

/**
 * The projects a BOM is built in, and those only a draft brings it into (familyProjects(): a product's link, a product
 * BOM naming one while its product is linked to none, a kit nothing uses by its own project, a used sub-assembly by what
 * uses it), the same on the BOMs page, in the editor and its picker and in the MCP; else a product BOM's own.
 */
export function bomProjects(px: ProjectIndex, bom: Pick<BomRow, "shopify_product_id" | "item_id" | "project_ids">): Pick<PartProjects, "inUse" | "onDraft"> {
  const family = familyProjects(reachOf(px), familyKey(bom));
  if (family.inUse.length || family.onDraft.length || !bom.shopify_product_id) return family;
  const own = px.linkByProduct.get(bom.shopify_product_id)?.project_id ?? bom.project_ids[0];
  return { inUse: own ? [own] : [], onDraft: [] };
}

/**
 * A link for a gap. In the workspace an item and a spec open in the side pane, right there (?open=, ?spec=);
 * everything else goes to the page that fixes it.
 */
export function gapLink(px: ProjectIndex, project: Pick<Project, "id" | "code">, t: GapTarget, here: string): string {
  if (t.kind === "item") return `${here}${here.includes("?") ? "&" : "?"}open=${t.id}`;
  if (t.kind === "spec") return `${here}${here.includes("?") ? "&" : "?"}spec=${t.id}`;
  if (t.kind === "basics") return `${here}${here.includes("?") ? "&" : "?"}edit=basics`;
  if (t.kind === "bom") return `${here.split("?")[0]}?tab=build&bom=${t.id}`; // in the project, not the BOMs page
  return targetPath(px, project, t);
}
